/**
 * Canvas v1: the stored shape of presentations and PDF pages today (`canvasPage` > `canvasItem`
 * nodes inside a block document). Stays writable until slides-editor and doc-templates migrate
 * their kinds to canvas v2 in wave 2 (`./canvas.ts` converts both ways).
 * Owner: doc-engine (wave 1), slides-editor (wave 2).
 */
import { z } from 'zod';
import { ThemeKey } from './design';
import { FieldPath } from '../filter';
import type { DocumentNode } from './node';

export const PageKind = z.enum(['page', 'presentation', 'pdf']);
export type PageKind = z.infer<typeof PageKind>;
const color = z.string().regex(/^#[a-fA-F0-9]{6}$/);
export const CanvasFrame = z
  .object({
    id: z.string().min(1).max(80),
    title: z.string().max(160),
    width: z.number().min(320).max(2000),
    height: z.number().min(320).max(2000),
    background: color,
    notes: z.string().max(10000).optional(),
    theme: ThemeKey.optional(),
  })
  .strict();
export type CanvasFrame = z.infer<typeof CanvasFrame>;
export const CanvasItem = z
  .object({
    id: z.string().min(1).max(80),
    x: z.number().min(0).max(2000),
    y: z.number().min(0).max(2000),
    width: z.number().min(1).max(2000),
    height: z.number().min(1).max(2000),
    fontSize: z.number().min(8).max(160),
    fontFamily: z.enum(['Arial', 'Georgia', 'Courier New']),
    color,
    background: z.union([color, z.literal('transparent')]),
    bold: z.boolean(),
    italic: z.boolean(),
    align: z.enum(['left', 'center', 'right']),
    shape: z.enum(['rectangle', 'ellipse']),
    border: color,
    borderWidth: z.number().min(0).max(20),
    radius: z.number().min(0).max(200).optional(),
    padding: z.number().min(0).max(100).optional(),
    lineHeight: z.number().min(1).max(2).optional(),
    verticalAlign: z.enum(['top', 'middle', 'bottom']).optional(),
    locked: z.boolean().optional(),
  })
  .strict();
export type CanvasItem = z.infer<typeof CanvasItem>;
export const CalculationTable = z
  .object({
    columns: z
      .array(
        z
          .object({
            key: z.string().regex(/^[a-z][a-z0-9_]{0,30}$/),
            label: z.string().max(100),
            format: z.enum(['text', 'number', 'currency']),
            formula: z.string().max(500).optional(),
            total: z.boolean().optional(),
          })
          .strict(),
      )
      .min(1)
      .max(8),
    rows: z.array(z.record(z.string(), z.union([z.string().max(2000), z.number().finite()]))).max(100),
    currency: z.string().regex(/^[A-Z]{3}$/),
  })
  .strict()
  .superRefine((table, ctx) => {
    if (new Set(table.columns.map((c) => c.key)).size !== table.columns.length)
      ctx.addIssue({ code: 'custom', message: 'Column keys must be unique' });
  });
export type CalculationTable = z.infer<typeof CalculationTable>;
export const RecordValue = z
  .object({
    field: z.union([FieldPath, z.literal('')]).default(''),
    expression: z.string().trim().max(4000).optional(),
    label: z.string().max(100),
    public: z.boolean().default(false),
    format: z.enum(['auto', 'text', 'number', 'currency', 'percent', 'date', 'datetime']).optional(),
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .optional(),
    decimals: z.number().int().min(0).max(8).optional(),
    fallback: z.string().max(500).optional(),
  })
  .strict()
  .refine((value) => Boolean(value.field || value.expression), 'Choose a field or enter an expression');
export type RecordValue = z.infer<typeof RecordValue>;
export function canvasFrames(doc: DocumentNode) {
  return doc.content?.filter((n) => n.type === 'canvasPage') ?? [];
}
export function canvasItemStyle(a: CanvasItem) {
  return {
    left: a.x,
    top: a.y,
    width: a.width,
    height: a.height,
    fontSize: a.fontSize,
    fontFamily: a.fontFamily,
    color: a.color,
    background: a.background,
    fontWeight: a.bold ? 700 : 400,
    fontStyle: a.italic ? 'italic' : 'normal',
    textAlign: a.align,
    borderRadius: a.shape === 'ellipse' ? '50%' : (a.radius ?? 0),
    padding: a.padding ?? 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent:
      a.verticalAlign === 'middle' ? 'center' : a.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
    border: `${a.borderWidth}px solid ${a.border}`,
    lineHeight: a.lineHeight ?? 1.25,
  } as const;
}
